// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the media model
//  ---------------------------------------------------------------------------
//  One question, asked with no DOM and no React: does this text name something
//  we can play or show, and what exactly would we frame to do it? md-board.jsx
//  draws the result and md-app.jsx writes the record; both ask this.
//
//  WHY IT IS ITS OWN MODULE, AND ITS OWN TEST. The answer this function gives
//  becomes the `src` of a LIVE THIRD-PARTY IFRAME. A record body is arbitrary
//  text the writer may have pasted from anywhere, so the distance between "what
//  was pasted" and "what we frame" is the whole safety of the feature. That
//  distance can only be asserted if it lives somewhere a test can reach without
//  a browser — the same argument md-link.ts makes for mdLinkHref, and this
//  module leans on that function rather than repeating it.
//
//  ── THE EMBED URL IS BUILT, NEVER BORROWED ────────────────────────────────
//  The obvious implementation puts the pasted URL straight into the frame. It
//  is wrong in a way nothing visible would ever reveal: a YouTube link copied
//  from a browser carries `si=`, a session identifier, and often a playlist and
//  a start time. Forwarding those hands a third party data the writer never
//  chose to send, from a page they think of as their journal.
//
//  So the only thing taken out of the pasted URL is the VIDEO ID, checked
//  against a shape, and everything framed is assembled here from constants.
//  Nothing a writer types can add a parameter to an embed.
//
//  ── WHAT IS REFUSED, AND WHY EACH ONE IS REAL ─────────────────────────────
//    • anything that is not https. mdLinkHref already refuses javascript: and
//      data: by parsing rather than prefix-testing (see its header — the URL
//      parser strips control characters before reading a scheme, so a
//      handwritten check disagrees with the browser about what the string
//      means). Plain http is refused on top of that: this app is served over
//      https, so an http frame is blocked as mixed content and accepting one
//      could only ever produce an empty box with no explanation;
//    • a host that is not EXACTLY on the list. `youtube.com.evil.test` contains
//      a permitted host as a substring, and a substring test would frame an
//      attacker's page under a name the allowlist appears to have approved;
//    • an id that is not shaped like one. Without this, any path segment
//      becomes an "id" and we build a confident embed URL for a video that does
//      not exist — a broken frame instead of an honest "this is just a link".
//
//  ── ON ITS OWN LINE, OR IT IS PROSE ───────────────────────────────────────
//  mdMediaScan only reads a line that is nothing but a URL. A record that
//  mentions a video in a sentence is writing ABOUT a video, and replacing that
//  paragraph with a player would take the person's own words off the screen.
//  This is the same restraint md-link.ts shows in autolinking only `https://`
//  and `www.` and never a bare dotted word.
// ════════════════════════════════════════════════════════════════════════════

/** What a piece of text turned out to name. `aspect` is null for anything whose
    shape is its own business — an image is whatever size it is. */
interface MdMedia {
  kind: 'video' | 'image';
  host: string;
  id: string;
  src: string;
  href: string;
  poster: string;
  aspect: number | null;
  /** Where to ask the host what this is called, or '' when nobody can be asked.
      An image has no such service — its name is in its own path. */
  oembed: string;
  /** A title we can know without asking anybody. '' when only the host knows. */
  title: string;
}

(function (g: any) {
  'use strict';

  /* A video is 16:9 and that is not a preference. Named once so the model, the
     default element size and any future card all mean the same rectangle. */
  const MD_MEDIA_ASPECT = 16 / 9;

  /* Exact hosts. A Set rather than a regex so there is no way to write one that
     accidentally matches a suffix — see the header. */
  const YT_HOSTS = new Set([
    'youtube.com', 'www.youtube.com', 'm.youtube.com',
    'youtu.be', 'www.youtu.be',
    'youtube-nocookie.com', 'www.youtube-nocookie.com',
  ]);
  const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

  /* Eleven characters of URL-safe base64. This has been YouTube's id shape for
     the whole life of the service. Being strict costs nothing when it is wrong:
     an unrecognised link stays a link, which is a graceful outcome, whereas a
     loose rule produces a frame confidently pointed at nothing. */
  const YT_ID = /^[A-Za-z0-9_-]{11}$/;
  const VIMEO_ID = /^\d{6,12}$/;

  /* No SVG. An <img> will not run script inside one, but an SVG is a document
     rather than a picture and the reasons to allow it here are weak. */
  const IMAGE_EXT = /\.(?:png|jpe?g|gif|webp|avif)$/i;

  const YT_EMBED = 'https://www.youtube-nocookie.com/embed/';
  const YT_POSTER_A = 'https://i.ytimg.com/vi/';
  const YT_POSTER_B = '/hqdefault.jpg';
  const VIMEO_EMBED = 'https://player.vimeo.com/video/';

  /* Where a host will tell us what a video is called. Both of these answer with
     CORS, so the page can ask directly and no server of ours is involved.

     THE URL WE ASK ABOUT IS THE ONE WE BUILT, never the one that was pasted.
     Same rule as the embed: the pasted string carries session and playlist
     parameters, and there is no reason for any of it to reach a third party. */
  const YT_OEMBED = 'https://www.youtube.com/oembed?format=json&url=';
  const YT_CANON = 'https://www.youtube.com/watch?v=';
  const VIMEO_OEMBED = 'https://vimeo.com/api/oembed.json?url=';
  const VIMEO_CANON = 'https://vimeo.com/';

  /* An image is the one kind that names itself: the last path segment is a
     filename somebody chose. Percent-escapes are decoded and the extension
     dropped, so `my%20holiday.jpg` becomes "my holiday" — and when there is
     nothing usable there, '' rather than a guess. */
  function imageTitle(u: URL): string {
    const last = segments(u.pathname).pop() || '';
    let name = last;
    try { name = decodeURIComponent(last); } catch (e) { /* keep it raw */ }
    return name.replace(IMAGE_EXT, '').replace(/[_-]+/g, ' ').trim();
  }

  /** The path split into non-empty segments — `/embed/abc/` → ['embed','abc']. */
  function segments(pathname: string): string[] {
    return pathname.split('/').filter(Boolean);
  }

  function youtubeId(u: URL): string {
    const seg = segments(u.pathname);
    /* youtu.be puts the id where a path normally goes and has no other shape,
       so the first segment IS the id or the link is not a video. */
    if (u.hostname === 'youtu.be' || u.hostname === 'www.youtu.be') return seg[0] || '';
    if (seg[0] === 'watch') return u.searchParams.get('v') || '';
    /* `/shorts/`, `/embed/` and the legacy `/v/` all name the id second. Listed
       rather than "second segment of anything", so `/feed/subscriptions` is not
       read as a video called subscriptions. */
    if (seg[0] === 'shorts' || seg[0] === 'embed' || seg[0] === 'v') return seg[1] || '';
    return '';
  }

  function vimeoId(u: URL): string {
    const seg = segments(u.pathname);
    if (seg[0] === 'video') return seg[1] || '';       // player.vimeo.com/video/<id>
    return seg[0] || '';                                // vimeo.com/<id>
  }

  /** What this text names, or null when it names nothing we can show. */
  function mdMediaParse(raw: unknown): MdMedia | null {
    /* mdLinkHref is the authority on what is safe to open, and it is asked
       FIRST — before anything here looks at the string — so there is exactly one
       implementation of scheme handling in the app rather than two that can
       drift apart. */
    const href = g.mdLinkHref ? g.mdLinkHref(raw) : null;
    if (!href) return null;

    let u: URL;
    try { u = new URL(href); } catch (e) { return null; }
    if (u.protocol !== 'https:') return null;

    const host = u.hostname.toLowerCase();

    if (YT_HOSTS.has(host)) {
      const id = youtubeId(u);
      if (!YT_ID.test(id)) return null;
      return {
        kind: 'video', host: 'youtube', id,
        src: YT_EMBED + id,
        poster: YT_POSTER_A + id + YT_POSTER_B,
        oembed: YT_OEMBED + encodeURIComponent(YT_CANON + id),
        title: '',
        href, aspect: MD_MEDIA_ASPECT,
      };
    }

    if (VIMEO_HOSTS.has(host)) {
      const id = vimeoId(u);
      if (!VIMEO_ID.test(id)) return null;
      return {
        kind: 'video', host: 'vimeo', id,
        src: VIMEO_EMBED + id,
        poster: '',                  // Vimeo has no poster URL we can build offline
        oembed: VIMEO_OEMBED + encodeURIComponent(VIMEO_CANON + id),
        title: '',
        href, aspect: MD_MEDIA_ASPECT,
      };
    }

    /* An image is recognised by its own address rather than by its host, so no
       allowlist applies — and none would help, because the whole point is that
       it can be anywhere. It is drawn in an <img>, which cannot run script, so
       the exposure is the request itself rather than the content. The CSP names
       what may be fetched (see public/_headers). */
    if (IMAGE_EXT.test(u.pathname)) return mdMediaImage(href);

    return null;
  }

  /** The picture a writer SAID this address is.
      mdMediaParse asks the ADDRESS and believes only an extension it knows;
      this is the other door, and md-embed.ts opens it for the `![…](…)` form —
      markdown's own way of writing "show this", where the writer has told us
      what the thing is and the address has no say in the matter.

      It exists because most pictures on the web do not end in `.png`. A CDN
      serves `/photo-1682…?auto=format&w=1200`, and an app that can only show a
      picture whose path happens to carry an extension is an app that cannot
      show most pictures.

      Everything that makes the parsed kind safe is unchanged and asked in the
      same order: mdLinkHref first, then https only. What is dropped is a guess
      about content type, which was never a safety property — an <img> cannot
      execute what it is handed, whatever the server sends back. */
  function mdMediaImage(raw: unknown): MdMedia | null {
    const href = g.mdLinkHref ? g.mdLinkHref(raw) : null;
    if (!href) return null;
    let u: URL;
    try { u = new URL(href); } catch (e) { return null; }
    if (u.protocol !== 'https:') return null;
    /* The image keeps its query string. Unlike an embed, the query is part of
       the address of the picture — a signed URL or a size parameter — and
       stripping it fetches a different thing, or nothing. */
    return { kind: 'image', host: u.hostname.toLowerCase(), id: '', src: href,
      poster: href, href, aspect: null, oembed: '', title: imageTitle(u) };
  }

  /** The media a record shows, or null. Only a line that is NOTHING but a URL
      counts — see the header. */
  function mdMediaScan(text: unknown): MdMedia | null {
    if (typeof text !== 'string' || !text) return null;
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.indexOf(' ') >= 0) continue;   // blank, or prose
      const found = mdMediaParse(trimmed);
      if (found) return found;
    }
    return null;
  }

  /* ── asking the host what it is called ────────────────────────────────────
     The one thing in this module that touches the network, kept at the bottom
     and kept separate: everything above is pure and answerable in a test, and
     this is not. Callers must treat it as best-effort.

     IT NEVER THROWS AND IT NEVER HANGS. A title is a nicety — the record exists
     and the video plays without one — so every failure path ends at '' rather
     than at an error somebody has to handle: no network, a host that is down, a
     response that is not JSON, a CSP that forbids the call, or simply an answer
     that takes too long. The timeout matters most: without it a hung request
     leaves a promise nothing ever settles.

     The reply is somebody else's JSON, so `title` is taken only if it really is
     a string, and trimmed to the same 60 characters a record's name allows. */
  const OEMBED_TIMEOUT_MS = 6000;
  const TITLE_MAX = 60;

  async function mdMediaTitle(media: unknown): Promise<string> {
    const m = media as MdMedia | null;
    if (!m || typeof m !== 'object') return '';
    if (m.title) return m.title;                    // an image knows its own name
    if (!m.oembed || typeof fetch !== 'function') return '';
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => ctl && ctl.abort(), OEMBED_TIMEOUT_MS);
    try {
      const res = await fetch(m.oembed, {
        signal: ctl ? ctl.signal : undefined,
        /* No credentials, ever. This is a request to a third party about
           something the writer pasted; it must not carry their cookies. */
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
      if (!res.ok) return '';
      const data = await res.json();
      const title = data && typeof data.title === 'string' ? data.title.trim() : '';
      return title.slice(0, TITLE_MAX);
    } catch (e) {
      return '';
    } finally {
      clearTimeout(timer);
    }
  }

  g.MD_MEDIA_ASPECT = MD_MEDIA_ASPECT;
  g.mdMediaParse = mdMediaParse;
  g.mdMediaImage = mdMediaImage;
  g.mdMediaScan = mdMediaScan;
  g.mdMediaTitle = mdMediaTitle;

  // CommonJS escape hatch, following md-link.ts and md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = { MD_MEDIA_ASPECT, mdMediaParse, mdMediaImage, mdMediaScan, mdMediaTitle };
})(typeof window !== 'undefined' ? window : globalThis);
